import { memo, useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { joinURL, resolveURL, withoutHost } from 'ufo'
import type { Media } from 'src/services/apollo/generated/graphql'
import { config } from 'src/utils/config'
import { ORIGIN } from 'src/utils/constants'
import { getSmartThumbnailUrl } from 'src/utils/media'

const SITE_NAME = 'PixAI'
const ENV_STR = `${config.isDev ? '[DEV]' : ''}${SITE_NAME}`
const CANONICAL_ORIGIN = ORIGIN

export function MetaTitle(props: {
  content?: string
  options?: {
    suffix?: string
  }
}) {
  const { content, options } = props

  const title = useMemo(() => {
    const suffix = `${ENV_STR}${options?.suffix ? ` - ${options?.suffix}` : ''}`
    if (content) return content + ' | ' + suffix
    return suffix
  }, [content, options?.suffix])

  // Twitter Card has limited space and already shows "来自 pixai.art" at the bottom,
  // so we use only the content (artwork title) without the platform suffix.
  const twitterTitle = content || title

  return (
    <Helmet>
      <title>{title}</title>
      <meta property="og:title" content={title} />
      <meta name="twitter:title" content={twitterTitle} />
    </Helmet>
  )
}

export function MetaDescription(props: { content?: string }) {
  const { content } = props
  return (
    <Helmet>
      <meta name="description" content={content} />
      <meta property="og:description" content={content} />
      <meta name="twitter:description" content={content} />
    </Helmet>
  )
}

export function MetaOgUrl(props: { pathname?: string }) {
  const url = joinURL(ORIGIN, props.pathname ?? '')
  return (
    <Helmet>
      <meta property="og:url" content={url} />
      <meta name="twitter:url" content={url} />
    </Helmet>
  )
}

export function MetaThumbnail(props: { media?: Media; publicUrl?: string }) {
  const thumbnailUrl = getSmartThumbnailUrl(props.media)
  if (thumbnailUrl || props.publicUrl) {
    return (
      <Helmet>
        <meta property="og:image" content={thumbnailUrl ?? props.publicUrl} />
        <meta name="twitter:image" content={thumbnailUrl ?? props.publicUrl} />
      </Helmet>
    )
  }
  return null
}

/**
 * Set canonical URL for SEO
 *
 * Here we did not use <Helmet> from react-helmet-async because it
 * does not work well with server-side rendered canonical URL.
 *
 * Consider the following situation:
 * 1. Server side has rendered a canonical URL
 * 2. Client side also rendered a canonical URL
 * Then the canonical URL will be duplicated.
 *
 * We tried to use hacks like `data-rh=1` to prevent <Helmet> from duplicating.
 * But it produces another problem. If a page does not have canonical URL
 * configured in Helmet, then the server-side rendered canonical URL will be removed.
 *
 * So finally we decided to use plain JavaScript to set canonical URL and do some dedup.
 *
 * In the future, we should not set canonical URL on client side.
 */
export const MetaCanonicalURL = memo((props: { href: string }) => {
  useEffect(() => {
    if (props.href) {
      setCanonicalURL(props.href)
    }
  }, [props])

  return null
})

export function setCanonicalURL(href: string) {
  const element = document.querySelectorAll('link[rel="canonical"]')
  const canonicalUrl = resolveURL(CANONICAL_ORIGIN, withoutHost(href))

  if (element.length === 0) {
    const link = document.createElement('link')
    link.setAttribute('rel', 'canonical')
    link.setAttribute('href', canonicalUrl)
    document.head.appendChild(link)
  } else {
    if (element.length > 1) {
      console.warn('Multiple canonical links found')
    }
    element.forEach(linkElement => {
      linkElement.setAttribute('href', canonicalUrl)
    })
  }
}

export function DefaultHelmetTags() {
  return (
    <Helmet>
      <meta
        name="description"
        content="PixAI is a community where you can share and browse artworks generated by AI with text. You can also generate your own artworks here."
      />
      <meta property="og:url" content={ORIGIN} />
      <meta
        property="og:image"
        content="https://imagedelivery.net/5ejkUOtsMH5sf63fw6q33Q/0d8c0aee-c2eb-436a-a70d-262b4a9a0700/thumbnail"
      />
      <meta
        name="twitter:title"
        content="PixAI - Anime AI Art Generator for Free"
      />
      <meta
        name="twitter:description"
        content="PixAI is a community where you can share and browse artworks generated by AI with text. You can also generate your own artworks here."
      />
      <meta
        property="og:title"
        content="PixAI - Anime AI Art Generator for Free"
      />
      <meta
        property="og:description"
        content="PixAI is a community where you can share and browse artworks generated by AI with text. You can also generate your own artworks here."
      />
    </Helmet>
  )
}

export default function MetaTags(props: {
  title?: string
  titleOptions?: {
    suffix?: string
  }
  description?: string
  thumbnailMedia?: Media
  thumbnailUrl?: string
  ogUrlPathname?: string
  canonicalURL?: string
}) {
  const {
    title,
    titleOptions,
    description,
    thumbnailMedia,
    thumbnailUrl,
    ogUrlPathname,
    canonicalURL,
  } = props
  return (
    <>
      {title && <MetaTitle content={title} options={titleOptions} />}
      {description && <MetaDescription content={description} />}
      {(thumbnailMedia || thumbnailUrl) && (
        <MetaThumbnail media={thumbnailMedia} publicUrl={thumbnailUrl} />
      )}
      {/* TODO: og-url may use canonical URL by default */}
      {ogUrlPathname && <MetaOgUrl pathname={ogUrlPathname} />}
      {canonicalURL && <MetaCanonicalURL href={canonicalURL} />}
    </>
  )
}

export function DefaultMetaTitle() {
  const { t } = useTranslation(['common'])

  return <MetaTitle options={{ suffix: t('common:meta.title.home-suffix') }} />
}

/**
 * Filters URL parameters for canonical URLs, keeping only SEO-relevant parameters.
 * Preserves search terms (q, keyword) and pagination parameters (before, after)
 * while removing tracking and other non-canonical parameters.
 *
 * Also normalizes paths:
 * - /artworks/models, /artworks/animations → /artworks
 * - /likes, /likes/liked-model, /likes/liked-lora → /artworks
 * - /decorations, /decorations/* → /artworks
 * - /collection/:id → /artworks
 */

const validSearchParams = ['q', 'keyword', 'before', 'after']

// Sub-tabs that should be normalized to /artworks
const artworksSubTabs = ['models', 'animations']

// Profile tabs that should be normalized to /artworks
const profileTabsToArtworks = ['likes', 'decorations']

export function getCanonicalURL(url: string) {
  const canonicalURL = new URL(url)
  const searchParams = new URLSearchParams(canonicalURL.search)
  const keys = Array.from(searchParams.keys())

  for (const key of keys) {
    if (!validSearchParams.includes(key)) {
      searchParams.delete(key)
    }
  }

  // Normalize collection to /artworks
  // e.g., /@username/collection/123456 → /@username/artworks
  canonicalURL.pathname = canonicalURL.pathname.replace(
    /\/collection\/[^/]+$/,
    '/artworks',
  )

  // Normalize artworks sub-tabs to /artworks
  // e.g., /@username/artworks/models → /@username/artworks
  const artworksSubTabPattern = new RegExp(
    `/artworks/(${artworksSubTabs.join('|')})(?:/|$)`,
  )
  canonicalURL.pathname = canonicalURL.pathname.replace(
    artworksSubTabPattern,
    '/artworks',
  )

  // Normalize other profile tabs to /artworks
  // e.g., /@username/likes → /@username/artworks
  //       /@username/likes/liked-model → /@username/artworks
  //       /@username/decorations/badge → /@username/artworks
  const profileTabPattern = new RegExp(
    `/(${profileTabsToArtworks.join('|')})(?:/.*)?$`,
  )
  canonicalURL.pathname = canonicalURL.pathname.replace(
    profileTabPattern,
    '/artworks',
  )

  canonicalURL.search = searchParams.toString()
  // Remove hash from canonical URL
  canonicalURL.hash = ''
  return canonicalURL.href
}

export function DefaultMetaCanonicalURL() {
  const canonicalURL = getCanonicalURL(window.location.href)

  return <MetaCanonicalURL href={canonicalURL} />
}
